import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { apiService } from "../../services/api/apiService";
import { requestReactivation } from "../../services/api/userService";
import OAuthLogin from "./OAuthLogin";
import logger from "../../utils/logger";
import "../../styles/features/auth-unified.css";
import "../../styles/features/auth.css";

/**
 * Unified Authentication Component
 * Single page with all auth methods: Email/Password, Phone OTP, Google, Facebook
 * No tabs - all visible in sections
 */
function AuthUnified() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const login = auth?.login;
  // Always redirect to dashboard after login; specific deep links (e.g. /leagues/123)
  // are preserved only for resource pages, not bare list pages.
  const requestedFrom = location.state?.from || "";
  const isDeepLink = requestedFrom && requestedFrom !== "/" && !/^\/(leagues|tournaments|matches|teams|drop-in)\/?$/.test(requestedFrom);
  const from = isDeepLink ? requestedFrom : "/dashboard";

  // Form state
  const [emailForm, setEmailForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);

  // Messages
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // Deactivated-account reactivation state
  const [isDeactivated, setIsDeactivated] = useState(false);
  const [deactivatedEmail, setDeactivatedEmail] = useState("");
  const [reactivating, setReactivating] = useState(false);
  const [reactivateMsg, setReactivateMsg] = useState("");

  // Unverified account — show resend button instead of generic error
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [resendStatus, setResendStatus] = useState(""); // "" | "sending" | "sent" | "error"

  // Clear messages
  const clearMessages = () => {
    setError("");
    setMessage("");
  };

  // Handle email/password input change
  const handleEmailChange = (e) => {
    setEmailForm({ ...emailForm, [e.target.name]: e.target.value });
    clearMessages();
  };

  // Handle email/password login
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);

    logger.info("Email login attempt", { email: emailForm.email });

    try {
      const response = await apiService.loginUser(emailForm);

      // Production: token is in HttpOnly cookie (set by server, not in response body)
      // Development/local: token IS returned in response body — save it so
      // subsequent API calls can send it as Authorization: Bearer header
      const user = response.user || response.data?.user;
      const token = response.token || response.data?.token || null;

      if (response.success && user) {
        setMessage("Login successful! Redirecting...");
        logger.logUserAction("email_login_success", {
          email: emailForm.email,
          userName: user?.name,
        });

        // Save user data always; save token only when present (local dev)
        localStorage.setItem("user", JSON.stringify(user));
        if (token) {
          localStorage.setItem("token", token);
        } else {
          localStorage.removeItem("token"); // production: cookie handles auth
        }

        if (login) {
          login(null, user);
        }

        setTimeout(() => {
          navigate(from, { replace: true });
        }, 1000);
      } else {
        throw new Error("Login failed - no user data received");
      }
    } catch (err) {

      // Handle deactivated account specifically
      if (err.response?.status === 403 && err.response?.data?.code === "ACCOUNT_DEACTIVATED") {
        setIsDeactivated(true);
        setDeactivatedEmail(err.response.data.email || emailForm.email);
        return;
      }

      // Handle unverified email — show resend button
      if (err.response?.data?.code === "EMAIL_NOT_VERIFIED") {
        setUnverifiedEmail(err.response.data.email || emailForm.email);
        setError("");
        return;
      }

      // Unknown account and wrong password share one message so the page
      // does not reveal whether an account exists.
      if (["USER_NOT_FOUND", "INVALID_CREDENTIALS"].includes(err.response?.data?.code)) {
        setError("Invalid email or password");
        return;
      }

      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Login failed";

      setError(errorMsg);
      logger.error("Email login failed", {
        email: emailForm.email,
        error: errorMsg,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle reactivation request from login page
  const handleReactivate = async () => {
    setReactivating(true);
    setReactivateMsg("");
    try {
      await requestReactivation({ email: deactivatedEmail });
      setReactivateMsg("success");
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.message ||
        "Failed to send reactivation. Please try again or email hello@sportifide.com.";
      setReactivateMsg("error:" + msg);
    } finally {
      setReactivating(false);
    }
  };

  const handleDismissReactivation = () => {
    setIsDeactivated(false);
    setDeactivatedEmail("");
    setReactivateMsg("");
    setError("");
  };

  // Handle OAuth success
  const handleOAuthSuccess = (token, user) => {
    setMessage("Login successful! Redirecting...");
    
    // With Phase 1.1 HttpOnly cookies, token is in cookie (not localStorage)
    // Only store user data
    localStorage.setItem("user", JSON.stringify(user));

    if (login) {
      // Pass null for token since it's in HttpOnly cookie
      login(null, user);
    }

    setTimeout(() => {
      navigate(from, { replace: true });
    }, 1000);
  };



  return (
    <div className="auth-unified-container">
      {/* Main Login Card - Centered */}
      <div className="auth-card-unified">
        {/* Header */}
        <div className="auth-header-unified">
          <h1 className="auth-title-unified">Sportifide</h1>
          <p className="auth-subtitle-unified">Your Sports Community Platform</p>
        </div>

        {/* Error/Success Messages */}
        {error && !isDeactivated && (
          <div className="auth-alert-unified error-alert-unified" role="alert">
            <span className="alert-icon-unified">⚠️</span>
            <span className="alert-text-unified">
              {error}
              {error.includes("create one") && (
                <>
                  {" "}<Link to="/register" style={{ color: "#fd7e14", fontWeight: 700 }}>Register →</Link>
                </>
              )}
            </span>
          </div>
        )}
        {message && (
          <div className="auth-alert-unified success-alert-unified" role="alert">
            <span className="alert-icon-unified">✅</span>
            <span className="alert-text-unified">{message}</span>
          </div>
        )}

        {/* Unverified account banner */}
        {unverifiedEmail && (
          <div
            style={{
              background: "rgba(253,126,20,0.1)",
              border: "1.5px solid rgba(253,126,20,0.5)",
              borderRadius: 10,
              padding: "0.9rem 1rem",
              marginBottom: "1rem",
            }}
            role="alert"
          >
            <p style={{ color: "#fd7e14", fontWeight: 700, margin: "0 0 0.3rem", fontSize: "0.92rem" }}>
              📧 Email not verified
            </p>
            <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem", margin: "0 0 0.7rem" }}>
              The account for <strong>{unverifiedEmail}</strong> hasn’t been activated yet.
              Click below to resend the activation link.
            </p>
            <button
              className="auth-button-unified primary-button-unified"
              style={{ padding: "0.5rem 1rem", fontSize: "0.86rem", width: "auto",
                background: resendStatus === "sent" ? "#22c55e" : undefined }}
              disabled={resendStatus === "sending" || resendStatus === "sent"}
              onClick={async () => {
                setResendStatus("sending");
                try {
                  const { apiClient } = await import("../../services/api/apiService");
                  await apiClient.post("/auth/resend-verification", { email: unverifiedEmail });
                  setResendStatus("sent");
                } catch {
                  setResendStatus("error");
                }
              }}
            >
              {resendStatus === "sending" && "Sending…"}
              {resendStatus === "sent"    && "✅ Activation link sent! Check your inbox."}
              {resendStatus === "error"   && "Failed — try again"}
              {!resendStatus             && "Resend activation email"}
            </button>
          </div>
        )}

        {/* ── Deactivated Account Banner ── */}
        {isDeactivated && (
          <div
            style={{
              background: "rgba(253,126,20,0.12)",
              border: "1.5px solid #fd7e14",
              borderRadius: 10,
              padding: "1rem 1.1rem",
              marginBottom: "1rem",
            }}
            role="alert"
          >
            {reactivateMsg === "success" ? (
              <>
                <p style={{ color: "#4ade80", fontWeight: 700, margin: "0 0 0.4rem" }}>📧 Check your email</p>
                <p style={{ color: "#ccc", fontSize: "0.88rem", margin: "0 0 0.75rem" }}>
                  If your account can be reactivated, we've sent a link to <strong>{deactivatedEmail}</strong>.
                  Open it within 1 hour to choose a new password and reactivate your account.
                </p>
                <button
                  className="auth-button-unified primary-button-unified"
                  style={{ padding: "0.5rem 1rem", fontSize: "0.88rem" }}
                  onClick={handleDismissReactivation}
                >
                  Back to Login
                </button>
              </>
            ) : (
              <>
                <p style={{ color: "#fd7e14", fontWeight: 700, margin: "0 0 0.35rem" }}>⏸ Account Deactivated</p>
                <p style={{ color: "#ccc", fontSize: "0.88rem", margin: "0 0 0.75rem" }}>
                  Your account (<strong>{deactivatedEmail}</strong>) has been deactivated.
                  Would you like us to email you a link to reactivate it?
                </p>
                {reactivateMsg.startsWith("error:") && (
                  <p style={{ color: "#ff6b6b", fontSize: "0.82rem", margin: "0 0 0.5rem" }}>
                    {reactivateMsg.replace("error:", "")}
                  </p>
                )}
                <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
                  <button
                    className="auth-button-unified primary-button-unified"
                    style={{ padding: "0.5rem 1rem", fontSize: "0.88rem", background: "linear-gradient(135deg,#fd7e14,#e05c00)" }}
                    onClick={handleReactivate}
                    disabled={reactivating}
                  >
                    {reactivating ? "Sending…" : "Email Me a Reactivation Link"}
                  </button>
                  <button
                    className="auth-button-unified"
                    style={{ padding: "0.5rem 1rem", fontSize: "0.88rem", background: "transparent", border: "1px solid rgba(255,255,255,0.3)" }}
                    onClick={handleDismissReactivation}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Email/Password Section */
        <div className="auth-section-unified email-section-unified">
          <h2 className="section-title-unified">📧 Email & Password</h2>

          <form onSubmit={handleEmailLogin} className="auth-form-unified">
            <div className="form-group-unified">
              <label htmlFor="email">Email or Username</label>
              <input
                id="email"
                name="email"
                type="text"
                placeholder="email or username"
                value={emailForm.email}
                onChange={handleEmailChange}
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>

            <div className="form-group-unified">
              <label htmlFor="password">Password</label>
              <div className="password-wrapper">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={emailForm.password}
                  onChange={handleEmailChange}
                  required
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="auth-button-unified primary-button-unified"
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <div className="form-links-unified">
            <Link to="/forgot-password" className="form-link-unified">
              Forgot Password?
            </Link>
          </div>
        </div>
}
        {/* Divider */}
        {/* Social Login Section — temporarily disabled */}
        {/* <div className="auth-divider-unified"><span>OR</span></div> */}
        {/* <div className="auth-method-section-unified social-method">
          <h3 className="method-title-unified">🌐 Social Login</h3>
          <p className="method-desc-unified">Quick login with Google or Facebook</p>
          <OAuthLogin onSuccess={handleOAuthSuccess} variant="compact" />
        </div> */}

        {/* Registration Section */}
        <div className="auth-register-section-unified">
          <p className="register-prompt-unified">
            New to Sportifide?{" "}
            <Link to="/register" className="link-button-unified">
              Create Account
            </Link>
          </p>
          <p className="register-prompt-unified">
            Just want to keep score?{" "}
            <Link to="/quick-score" className="link-button-unified">
              ⚡ Quick Score — no account needed
            </Link>
          </p>
        </div>

        {/* Contact / Support */}
        <div
          style={{
            textAlign: "center",
            padding: "0.75rem 0 0.25rem",
            fontSize: "0.8rem",
            color: "rgb(0, 0, 0)",
            borderTop: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          Need help?{" "}
          <a
            href="mailto:hello@sportifide.com"
            style={{ color: "#ff2903", textDecoration: "none" }}
          >
            hello@sportifide.com
          </a>
          {" · "}
          <Link to="/reactivate-account" style={{ color: "rgb(0, 0, 0)", textDecoration: "none" }}>
            Reactivate account
          </Link>
        </div>

        {/* Footer */}
        <div className="auth-footer-unified">
          <p className="footer-text-unified">
            By signing in, you agree to our{" "}
            <Link to="/terms" className="footer-link-unified">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="footer-link-unified">
              Privacy Policy
            </Link>
          </p>
          <p className="footer-text-unified" style={{ marginTop: "0.35rem", fontSize: "0.75rem", color: "rgb(0, 0, 0)" }}>
            © {new Date().getFullYear()} Sportifide. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

export default AuthUnified;
